import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { translations, detectLocale } from "./i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND}/api`;

export const LOGO_URL = "/assets/logo.png";
export const WHATSAPP_URL = "https://wa.me/message/MJMAHSPHBY4FB1";
export const TELEGRAM_URL = "https://t.me/sharkxelectric";
export const BAGS_PRICE_USD = 800;

export const websiteChannelHeaders = (channelOrigin = "website-public") => ({
  headers: {
    "X-SharkX-Channel-Origin": channelOrigin,
  },
});

const Ctx = createContext(null);

const mergeDeep = (base, override) => {
  if (Array.isArray(base) || Array.isArray(override)) return override ?? base;
  if (base && typeof base === "object" && override && typeof override === "object") {
    const out = { ...base };
    for (const [key, value] of Object.entries(override)) {
      out[key] = mergeDeep(base[key], value);
    }
    return out;
  }
  return override ?? base;
};

export const AppProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(detectLocale());
  const [autoDetected, setAutoDetected] = useState(false);
  const [country, setCountry] = useState(localStorage.getItem("sharkx_country") || "");
  const [currency, setCurrency] = useState(localStorage.getItem("sharkx_currency") || "USD");
  const [rates, setRates] = useState({ USD_TO_BRL: 5.5, USD_TO_USDT: 1.0 });
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("sharkx_cart") || "[]"));
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem("sharkx_wishlist") || "[]"));
  const [compare, setCompare] = useState(() => JSON.parse(localStorage.getItem("sharkx_compare") || "[]"));
  const [token, setToken] = useState(() => localStorage.getItem("sharkx_admin_token") || null);
  const [content, setContent] = useState({});

  // Geo detection on first visit
  useEffect(() => {
    const saved = localStorage.getItem("sharkx_locale");
    // Always fetch country (cheap) to drive BR delivery badge etc.
    axios.get(`${API}/geo/detect`).then((r) => {
      if (r.data.country) {
        setCountry(r.data.country);
        localStorage.setItem("sharkx_country", r.data.country);
      }
      if (!saved && r.data.locale && translations[r.data.locale]) {
        setLocaleState(r.data.locale);
        localStorage.setItem("sharkx_locale", r.data.locale);
        if (r.data.currency) {
          setCurrency(r.data.currency);
          localStorage.setItem("sharkx_currency", r.data.currency);
        }
      }
      setAutoDetected(true);
    }).catch(() => setAutoDetected(true));
  }, []);

  // Fetch rates + content + site settings (GA, IG handle)
  useEffect(() => {
    axios.get(`${API}/rates`).then((r) => setRates(r.data)).catch(() => {});
    axios.get(`${API}/content`).then((r) => setContent(r.data || {})).catch(() => {});
    axios.get(`${API}/site/settings`).then((r) => {
      const gaId = r.data?.ga_measurement_id;
      if (gaId && /^G-[A-Z0-9]+$/i.test(gaId) && !window.__ga_loaded) {
        window.__ga_loaded = true;
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag("js", new Date());
        gtag("config", gaId, { send_page_view: true });
      }
    }).catch(() => {});
  }, []);

  // Currency auto-follows locale (unless user overrode manually — stored separately)
  const setLocale = (l) => {
    setLocaleState(l);
    localStorage.setItem("sharkx_locale", l);
    const newCurrency = l === "pt" ? "BRL" : "USD";
    setCurrency(newCurrency);
    localStorage.setItem("sharkx_currency", newCurrency);
  };

  useEffect(() => { localStorage.setItem("sharkx_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("sharkx_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("sharkx_compare", JSON.stringify(compare)); }, [compare]);

  const t = mergeDeep(translations.en, translations[locale] || {});

  const addToCart = (item) => setCart((c) => [...c, { ...item, key: Date.now() + Math.random() }]);
  const removeFromCart = (key) => setCart((c) => c.filter((i) => i.key !== key));
  const clearCart = () => setCart([]);

  const toggleWishlist = (slug) => setWishlist((w) => (w.includes(slug) ? w.filter((s) => s !== slug) : [...w, slug]));
  const toggleCompare = (slug) => setCompare((c) => { if (c.includes(slug)) return c.filter((s) => s !== slug); if (c.length >= 3) return c; return [...c, slug]; });

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setToken(res.data.token);
    localStorage.setItem("sharkx_admin_token", res.data.token);
    return res.data;
  };
  const logout = () => { setToken(null); localStorage.removeItem("sharkx_admin_token"); };
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  // Currency-aware formatter
  const formatPrice = (product_or_usd) => {
    let usd = 0, brlOverride = null;
    if (typeof product_or_usd === "object" && product_or_usd !== null) {
      usd = Number(product_or_usd.base_price || 0);
      brlOverride = product_or_usd.price_brl_override;
    } else {
      usd = Number(product_or_usd || 0);
    }
    if (currency === "BRL") {
      const brl = brlOverride != null ? Number(brlOverride) : usd * rates.USD_TO_BRL;
      return brl.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
    }
    return usd.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  };

  // Get overridable content (key:string, field:'text'|'image'|'video', fallback)
  const getContent = (key, field = "text", fallback = "") => {
    return content?.[key]?.[locale]?.[field] || content?.[key]?.["en"]?.[field] || fallback;
  };

  return (
    <Ctx.Provider value={{
      locale, setLocale, t, currency, setCurrency, rates, autoDetected, country,
      cart, addToCart, removeFromCart, clearCart,
      wishlist, toggleWishlist,
      compare, toggleCompare,
      token, login, logout, authHeaders,
      formatPrice, getContent, content, setContent,
    }}>
      {children}
    </Ctx.Provider>
  );
};

export const useApp = () => useContext(Ctx);

export const computeItemPrice = (product, options) => {
  let p = Number(product.base_price || 0);
  if (options?.battery && product.battery_options) {
    const b = product.battery_options.find((x) => x.label === options.battery);
    if (b) p += Number(b.price_delta || 0);
  }
  if (options?.carbon) p += Number(product.carbon_upcharge || 0);
  if (options?.bags) p += BAGS_PRICE_USD;
  return p;
};
